import React, { FC } from "react";

import { Breadcrumb } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { Link, useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import ProjectsService from "@/services/ProjectsService";

import { BreadcrumbSelect } from "@/components/molecules/BreadcrumbSelect/BreadcrumbSelect";

import { TProject } from "@/types";

interface IProjectBreadcrumbProps {
	project: Pick<TProject, "id" | "client">;
}

/**
 * Clients › [client] › [project]: a project belongs to its client since
 * the views arrived, so that is what its trail says. The project select
 * jumps to a sibling - the client's other projects, or every project
 * one reaches for internal work (docs/modele.md, phase 6).
 */
export const ProjectBreadcrumb: FC<IProjectBreadcrumbProps> = ({ project }) => {
	const navigate = useNavigate();
	const clientId = project.client?.id;

	const { data: projects } = useQuery({
		queryKey: ["projects", "siblings", clientId ?? null],
		queryFn: () =>
			ProjectsService.list(clientId).then((resp: TProject[]) =>
				[...resp].sort((a, b) => a.name.localeCompare(b.name))
			),
	});

	return (
		<Breadcrumb
			className="text-sm"
			items={[
				{
					label: (
						<Link to="/clients" className="text-neutral-600">
							{t("clients.title")}
						</Link>
					),
				},
				{
					label: project.client?.canOpen ? (
						<Link
							to={`/clients/${project.client.id}`}
							className="text-neutral-600"
						>
							{project.client.name}
						</Link>
					) : (
						<span className="text-neutral-500">
							{project.client?.name ?? t("projects.internal")}
						</span>
					),
				},
				{
					label: (
						<BreadcrumbSelect
							options={(projects ?? []).map((item) => ({
								label: item.name,
								value: item.id,
								color: item.color,
							}))}
							value={project.id}
							onChange={(value) => {
								if (value !== null)
									navigate(`/projects/${value}`);
							}}
						/>
					),
				},
			]}
		/>
	);
};
